import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { 
  Smartphone, Check, Loader2, MessageSquare, 
  Bell, Info, Save
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import PhoneNumberInput, { isValidPhone } from '@/components/PhoneNumberInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatsAppSettingsCard = ({ user, onUpdate }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [phone, setPhone] = useState('');
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize from user data
  useEffect(() => {
    if (user) {
      setPhone(user.whatsapp_phone || user.phone || '');
      setWhatsappEnabled(user.whatsapp_enabled || false);
    }
  }, [user]);

  const handlePhoneChange = (nextPhone) => {
    setPhone(nextPhone);
    setHasChanges(true);
  };

  const handleToggleChange = (checked) => {
    setWhatsappEnabled(checked);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (whatsappEnabled && !isValidPhone(phone)) {
      toast.error('Informe um número de telefone válido para receber mensagens');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/users/${user.id}`, {
        phone: phone || null,
        whatsapp_enabled: whatsappEnabled,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Preferências de WhatsApp salvas!');
      setHasChanges(false);
      onUpdate?.();
    } catch (error) {
      console.error('Error saving WhatsApp settings:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <MessageSquare className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <CardTitle className="text-lg">Notificações WhatsApp</CardTitle>
              <CardDescription>
                Receba alertas e mensagens do Octopus no seu WhatsApp
              </CardDescription>
            </div>
          </div>
          {whatsappEnabled && isValidPhone(phone) ? (
            <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
              <Check className="w-3 h-3 mr-1" />
              Ativo
            </Badge>
          ) : (
            <Badge variant="secondary">
              Inativo
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Phone Number Input */}
        <div className="space-y-2">
          <Label>Número do WhatsApp</Label>
          <PhoneNumberInput
            value={phone}
            onChange={handlePhoneChange}
            idPrefix="whatsapp-phone"
          />
          <Input type="hidden" data-testid="whatsapp-phone-input" value={phone} readOnly />
          <p className="text-xs text-muted-foreground">
            Este número será usado para todas as notificações do sistema
          </p>
        </div>

        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-yellow-500" />
            <div>
              <p className="font-medium">Receber mensagens do Octopus</p>
              <p className="text-sm text-muted-foreground">
                Alertas, AI Helper, notificações do sistema
              </p>
            </div>
          </div>
          <Switch
            checked={whatsappEnabled}
            onCheckedChange={handleToggleChange}
            data-testid="whatsapp-enabled-switch"
          />
        </div>

        {/* Info about what messages will be sent */}
        {whatsappEnabled && (
          <Alert>
            <Info className="w-4 h-4" />
            <AlertDescription>
              <strong>Você receberá:</strong>
              <ul className="mt-2 space-y-1 list-disc list-inside text-sm">
                <li>Alertas de equipamentos (fora da faixa normal)</li>
                <li>Respostas do AI Helper</li>
                <li>Notificações importantes do sistema</li>
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Save Button */}
        {hasChanges && (
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="w-full"
            data-testid="save-whatsapp-settings-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Salvar Preferências
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default WhatsAppSettingsCard;
